import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { datesHelper } from "@/shared/utils";
import { wsdotTollRatesApiMeta } from "../apiMeta";
import {
  type TripRatesByDateInput,
  tripRatesByDateInputSchema,
} from "./shared/tollTripRates.input";
import {
  type TollTripsRates,
  tollTripsRatesSchema,
} from "./shared/tollTripRates.output";

/**
 * Metadata for the fetchTripRatesByDate endpoint
 */
export const tripRatesByDateMeta = {
  functionName: "fetchTripRatesByDate",
  endpoint: "/getTripRatesByDateAsJson?FromDate={FromDate}&ToDate={ToDate}",
  inputSchema: tripRatesByDateInputSchema,
  outputSchema: tollTripsRatesSchema.array(),
  sampleParams: {
    FromDate: datesHelper.yesterday(),
    ToDate: datesHelper.today(),
  },
  endpointDescription: "Get historical toll rates for a specified date range.",
} satisfies EndpointMeta<TripRatesByDateInput, TollTripsRates[]>;

/**
 * Fetch function for retrieving historical toll rates for a specified date range
 */
const { fetch, useHook } = createFetchAndHook<
  TripRatesByDateInput,
  TollTripsRates[]
>({
  api: wsdotTollRatesApiMeta,
  endpoint: tripRatesByDateMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/tollTripRates.endpoints").tollTripRatesGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving historical toll rates for a specified date range
 */
export const fetchTripRatesByDate: FetchFactory<
  TripRatesByDateInput,
  TollTripsRates[]
> = fetch;

/**
 * React Query hook for retrieving historical toll rates for a specified date range
 */
export const useTripRatesByDate: HookFactory<
  TripRatesByDateInput,
  TollTripsRates[]
> = useHook;
